import moment from 'moment';

export type HangTimeSource = 'head' | 'producer' | 'processor-default' | 'none';

export interface HangTimeOptionLike {
  durationDays?: number | null;
  isDefault?: boolean | null;
}

export interface HangTimeSpeciesConfigLike {
  species?: string | null;
  isEnabled?: boolean | null;
  options?: ReadonlyArray<HangTimeOptionLike | null> | null;
}

export interface HangTimePreferenceLike {
  vendorId?: { toString(): string } | string | null;
  species?: string | null;
  preferredDays?: number | string | null;
}

export interface ResolveHangTimeArgs {
  headHangTimeDays?: number | string | null;
  producerPreferences?: ReadonlyArray<HangTimePreferenceLike | null> | null;
  vendorId?: { toString(): string } | string | null;
  species?: string | null;
  hangTimeSettings?: ReadonlyArray<HangTimeSpeciesConfigLike | null> | null;
}

export interface ResolvedHangTime {
  days: number | undefined;
  source: HangTimeSource;
}

const toNumber = (
  v: number | string | null | undefined,
): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const toIdString = (
  v: { toString(): string } | string | null | undefined,
): string | undefined => {
  if (v === null || v === undefined) return undefined;
  return typeof v === 'string' ? v : v.toString();
};

/**
 * Resolves hang-time days using the canonical precedence:
 *   1. Per-head explicit value
 *   2. Producer preference (matched by vendorId + species) — only if it
 *      maps to a currently-offered option on the enabled species config
 *   3. Processor default option (isDefault on the species' enabled config)
 *
 * Returns `{ days: undefined, source: 'none' }` if no source produces a value.
 */
export function resolveHangTimeDays(
  args: ResolveHangTimeArgs,
): ResolvedHangTime {
  const headDays = toNumber(args.headHangTimeDays);
  if (headDays !== undefined) {
    return { days: headDays, source: 'head' };
  }

  const speciesConfig =
    args.species && args.hangTimeSettings
      ? args.hangTimeSettings.find(
          (s) =>
            s != null && s.isEnabled === true && s.species === args.species,
        )
      : null;
  const offeredDurations = new Set(
    (speciesConfig?.options ?? [])
      .map((o) => (o != null ? toNumber(o.durationDays) : undefined))
      .filter((d): d is number => d !== undefined),
  );

  const vendorIdStr = toIdString(args.vendorId);
  if (vendorIdStr && args.species && args.producerPreferences) {
    for (const pref of args.producerPreferences) {
      if (!pref) continue;
      const prefVendorId = toIdString(pref.vendorId);
      if (prefVendorId === vendorIdStr && pref.species === args.species) {
        const days = toNumber(pref.preferredDays);
        // Producer pref only counts if the processor still offers that
        // duration. Otherwise fall through to the processor default so
        // we don't bill against a disabled / removed option.
        if (days !== undefined && offeredDurations.has(days)) {
          return { days, source: 'producer' };
        }
      }
    }
  }

  if (speciesConfig) {
    const defaultOption = speciesConfig.options?.find(
      (o) => o != null && o.isDefault === true,
    );
    const days = toNumber(defaultOption?.durationDays);
    if (days !== undefined) {
      return { days, source: 'processor-default' };
    }
  }

  return { days: undefined, source: 'none' };
}

/**
 * Adds `hangTimeDays` to `killDate` using UTC arithmetic, matching the existing
 * formula reused across scheduling create / partial-update / kill-sheet paths.
 */
export function computeCutDate(
  killDate: Date | string,
  hangTimeDays: number,
): Date {
  return moment.utc(killDate).add(hangTimeDays, 'days').toDate();
}

export interface AutoSchedulingSpeciesConfigLike {
  species?: string | null;
  isEnabled?: boolean | null;
  harvestDateDays?: number | string | null;
  cutDateDays?: number | string | null;
}

export interface ComputeAutoSchedulingDatesArgs {
  /** The processor's per-species auto-scheduling config. */
  autoSchedulingSettings?: ReadonlyArray<AutoSchedulingSpeciesConfigLike | null> | null;
  /** The processor's per-species hang-time config (for cut-date refinement). */
  hangTimeSettings?: ReadonlyArray<HangTimeSpeciesConfigLike | null> | null;
  species?: string | null;
  /** The job's drop-off date — the anchor the harvest/cut offsets are added to. */
  dropoffDate?: Date | string | null;
  vendorId?: { toString(): string } | string | null;
  /** This head's explicit hang-time override, if any. */
  headHangTimeDays?: number | string | null;
  producerPreferences?: ReadonlyArray<HangTimePreferenceLike | null> | null;
}

export interface AutoSchedulingDates {
  killDate?: Date;
  cutDate?: Date;
  /** Resolved hang time, only set when hang time is enabled and resolvable. */
  hangTimeDays?: number;
}

/**
 * Derive a head's harvest (kill) and cut dates from the processor's
 * auto-scheduling config, mirroring the canonical job-create logic so every
 * head-creation/attachment path stamps dates identically:
 *
 *   killDate = dropoffDate + harvestDateDays
 *   cutDate  = killDate + (resolved hang time, else cutDateDays)
 *
 * Returns `{}` when auto-scheduling isn't enabled for the species (or the
 * drop-off date is missing) — callers then leave the dates unset, exactly as
 * job-create does. `hangTimeDays` is set only when hang time is enabled, and is
 * the resolved value (head/producer/default) or the `cutDateDays` fallback — not
 * necessarily an explicit per-head choice. Callers should still fall back to the
 * head's own value (`result.hangTimeDays ?? head.hangTimeDays`).
 */
export function computeAutoSchedulingDates(
  args: ComputeAutoSchedulingDatesArgs,
): AutoSchedulingDates {
  const {
    autoSchedulingSettings,
    hangTimeSettings,
    species,
    dropoffDate,
    vendorId,
    headHangTimeDays,
    producerPreferences,
  } = args;

  if (!species || dropoffDate == null) return {};

  // Trim + lowercase so whitespace/casing from CSV imports etc. still matches.
  const speciesLower = species.trim().toLowerCase();
  const auto = (autoSchedulingSettings ?? []).find(
    (s) =>
      s != null &&
      s.isEnabled === true &&
      (s.species ?? '').toLowerCase() === speciesLower,
  );
  if (!auto) return {};

  const harvestDateDays = toNumber(auto.harvestDateDays) ?? 0;
  const defaultCutDateDays = toNumber(auto.cutDateDays);

  const killDate = moment
    .utc(dropoffDate)
    .add(harvestDateDays, 'days')
    .toDate();
  let cutDate =
    defaultCutDateDays != null
      ? moment.utc(killDate).add(defaultCutDateDays, 'days').toDate()
      : undefined;

  let resolvedHangTimeDays: number | undefined;

  const hangTimeEnabled = (hangTimeSettings ?? []).some(
    (s) =>
      s != null &&
      s.isEnabled === true &&
      (s.species ?? '').toLowerCase() === speciesLower,
  );
  if (hangTimeEnabled) {
    const { days } = resolveHangTimeDays({
      headHangTimeDays,
      producerPreferences,
      vendorId,
      species: speciesLower,
      hangTimeSettings,
    });
    const resolvedDays = days ?? defaultCutDateDays;
    if (resolvedDays != null) {
      resolvedHangTimeDays = resolvedDays;
      cutDate = computeCutDate(killDate, resolvedDays);
    }
  }

  return { killDate, cutDate, hangTimeDays: resolvedHangTimeDays };
}
